import * as BABYLON from '@babylonjs/core';
import { DungeonEvents } from '../../types/game-events';
import { EventsManager } from '@game-libs/events-manager';

export interface DoorConfig {
  position: BABYLON.Vector3;
  rotation: number;
  cellSize: number;
  wallHeight: number;
  material: BABYLON.StandardMaterial;
  openMaterial: BABYLON.StandardMaterial;
  scene: BABYLON.Scene;
  camera: BABYLON.Camera;
  showDebug?: boolean;
  /** Called when player tries to open the door. Return true to allow open (key consumed), false to block. */
  onTryOpen?: () => boolean;
}

export class DungeonDoor {
  private mesh: BABYLON.Mesh;
  private collisionBox: BABYLON.Mesh;
  private isOpen: boolean = false;
  private hasBeenOpened: boolean = false; // Track if door was ever opened
  private position: BABYLON.Vector3;
  private rotation: number;
  private scene: BABYLON.Scene;
  private camera: BABYLON.Camera;
  private showDebug: boolean;
  private pointerObserver: BABYLON.Nullable<BABYLON.Observer<BABYLON.PointerInfo>> = null;
  private _disposed = false;
  private closedMaterial: BABYLON.StandardMaterial;
  private openMaterial: BABYLON.StandardMaterial;
  private onTryOpen: (() => boolean) | undefined;
  private eventsManager = EventsManager();


  constructor(config: DoorConfig) {
    this.position = config.position;
    this.rotation = config.rotation;
    this.scene = config.scene;
    this.camera = config.camera;
    this.showDebug = config.showDebug || false;
    this.closedMaterial = config.material;
    this.openMaterial = config.openMaterial;
    this.onTryOpen = config.onTryOpen;

    // Create the visual door mesh
    this.mesh = BABYLON.MeshBuilder.CreateGround(
      `door_${this.position.x}_${this.position.z}`,
      { width: config.cellSize, height: config.cellSize },
      config.scene
    );
    this.mesh.rotation.x = -Math.PI / 2; // Make it vertical
    this.mesh.rotation.y = this.rotation;
    this.mesh.material = this.closedMaterial;
    this.mesh.position = this.position.clone();
    this.mesh.renderingGroupId = 0;
    this.mesh.isPickable = true; // Make it pickable for clicks
    this.mesh.cullingStrategy = BABYLON.Mesh.OCCLUSION_TYPE_OPTIMISTIC;

    // Create a SOLID collision box that blocks the entire doorway
    // Make it bigger to ensure it properly blocks the tile
    const collisionThickness = config.cellSize * 0.8; // Much thicker to block passage
    const collisionWidth = config.cellSize;
    const collisionHeight = config.wallHeight;

    this.collisionBox = BABYLON.MeshBuilder.CreateBox(
      `doorCollision_${this.position.x}_${this.position.z}`,
      {
        width: this.rotation === 0 ? collisionThickness : collisionWidth,
        height: collisionHeight,
        depth: this.rotation === 0 ? collisionWidth : collisionThickness
      },
      config.scene
    );
    this.collisionBox.position = this.position.clone();
    this.collisionBox.checkCollisions = true;
    this.collisionBox.isVisible = this.showDebug; // Show collision box if debug enabled
    this.collisionBox.isPickable = true; // Make collision box pickable too

    // Debug: Make collision box semi-transparent red when visible
    if (this.showDebug) {
      const debugMat = new BABYLON.StandardMaterial(`doorDebugMat_${this.position.x}_${this.position.z}`, config.scene);
      debugMat.diffuseColor = new BABYLON.Color3(1, 0, 0);
      debugMat.alpha = 0.5;
      this.collisionBox.material = debugMat;
    }
    // Door instantiated at position with rotation; consider emitting e.g. DUNGEON_DOOR_SPAWNED here.

    // Set up pointer observable to handle clicks on this specific door
    this.pointerObserver = this.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== BABYLON.PointerEventTypes.POINTERDOWN) return;

      const evt = pointerInfo.event;
      const canvas = this.scene.getEngine().getRenderingCanvas();
      if (!canvas || !evt) return;

      // Get click coordinates
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (evt.clientX - rect.left) * scaleX;
      const y = (evt.clientY - rect.top) * scaleY;

      // Pick to see what was clicked
      const pickInfo = this.scene.pick(x, y);
      // Optional: emit click/pick event when showDebug or for analytics.

      // Only toggle if THIS door's mesh or collision box was clicked
      if (!pickInfo.hit) return;
      if (pickInfo.pickedMesh !== this.mesh && pickInfo.pickedMesh !== this.collisionBox) return;

      // Check if player is facing the door
      if (!this.isPlayerFacing(5)) {
        // Player clicked door but not facing it; consider emitting e.g. DUNGEON_DOOR_CLICK_NOT_FACING.
        return;
      }
      // Require a key: consume from run-collected keys first, else from global keys; block if none.
      if (this.onTryOpen && !this.onTryOpen()) {
        this.eventsManager.emit(DungeonEvents.DOOR_LOCKED);
        return;
      }
      this.toggle();
    });
  }

  /**
   * Check if player is facing this door using raycast
   */
  private isPlayerFacing(maxDistance: number = 5): boolean {
    const ray = this.camera.getForwardRay(maxDistance);

    if (!ray) return false;

    const hit = this.scene.pickWithRay(ray, (mesh) => {
      return mesh === this.mesh || mesh === this.collisionBox;
    });

    return hit?.hit || false;
  }

  /**
   * Opens the door - makes it passable
   */
  public open(): void {
    if (this.isOpen) return;

    this.eventsManager.emit(DungeonEvents.OPEN_DOOR);
    this.isOpen = true;
    this.hasBeenOpened = true; // Mark that door has been opened
    // Emit e.g. DUNGEON_DOOR_OPENED here for SFX/analytics when a gray door is opened.

    // Disable collision
    this.collisionBox.checkCollisions = false;

    // Change to open door texture
    this.mesh.material = this.openMaterial;

    // No longer interactable; stop listening to pointer events
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }
  }

  /**
   * Toggles the door state (but only opens once - cannot be closed)
   */
  public toggle(): void {
    if (!this.hasBeenOpened) {
      this.open();
    } else {
      // Door already open (cannot be closed); consider emitting if needed.
    }
  }

  public getIsOpen(): boolean {
    return this.isOpen;
  }

  public getPosition(): BABYLON.Vector3 {
    return this.position;
  }

  public getPickableMeshes(): BABYLON.Mesh[] {
    return [this.mesh, this.collisionBox];
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    // Remove pointer observer
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    this.mesh.dispose();
    this.collisionBox.dispose();
  }
}